import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface SessionsDistributionChartsProps {
  days?: number;
  onDaysChange?: (days: number) => void;
}

export default function SessionsDistributionCharts({
  days = 7,
  onDaysChange
}: SessionsDistributionChartsProps) {
  const [chartView, setChartView] = useState<'hourly' | 'weekly'>('hourly');

  const { data, isLoading, error } = useQuery({
    queryKey: ['hourlyDistribution', days],
    queryFn: () => statsApi.getHourlyDistribution(days),
    refetchInterval: 60000, // Обновляем каждую минуту
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Ошибка загрузки данных: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning">
        Нет данных для отображения
      </Alert>
    );
  }

  // Prepare data for hourly chart
  const hourlyData = data.by_hour.map(item => ({
    hour: `${item.hour}:00`,
    total: item.count,
    successful: item.successful,
    failed: item.failed,
    successRate: item.success_rate,
  }));

  // Prepare data for weekly chart
  const weeklyData = data.by_day_of_week.map(item => ({
    day: item.day_name,
    total: item.count,
    successful: item.successful,
    failed: item.failed,
    successRate: item.success_rate,
  }));

  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: 'hourly' | 'weekly' | null
  ) => {
    if (newView !== null) {
      setChartView(newView);
    }
  };

  const handleDaysChange = (event: any) => {
    const newDays = event.target.value;
    if (onDaysChange) {
      onDaysChange(newDays);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="h2" fontWeight={600}>
          Распределение обработок
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Период</InputLabel>
            <Select
              value={days}
              onChange={handleDaysChange}
              label="Период"
            >
              <MenuItem value={7}>7 дней</MenuItem>
              <MenuItem value={14}>14 дней</MenuItem>
              <MenuItem value={30}>30 дней</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={chartView}
            exclusive
            onChange={handleViewChange}
            size="small"
          >
            <ToggleButton value="hourly">
              По часам
            </ToggleButton>
            <ToggleButton value="weekly">
              По дням недели
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {chartView === 'hourly'
                  ? `Обработки по часам суток (за последние ${days} дней)`
                  : `Обработки по дням недели (за последние ${days} дней)`
                }
              </Typography>

              <ResponsiveContainer width="100%" height={450}>
                <BarChart
                  data={chartView === 'hourly' ? hourlyData : weeklyData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={chartView === 'hourly' ? 'hour' : 'day'}
                    angle={chartView === 'hourly' ? -45 : 0}
                    textAnchor={chartView === 'hourly' ? 'end' : 'middle'}
                    height={chartView === 'hourly' ? 80 : 60}
                  />
                  <YAxis />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '10px'
                          }}>
                            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>
                              {chartView === 'hourly' ? `Час: ${label}` : label}
                            </p>
                            <p style={{ margin: '4px 0', color: '#2e7d32' }}>
                              Успешные: {data.successful} ({data.total > 0 ? ((data.successful / data.total) * 100).toFixed(1) : 0}%)
                            </p>
                            <p style={{ margin: '4px 0', color: '#d32f2f' }}>
                              Ошибки: {data.failed} ({data.total > 0 ? ((data.failed / data.total) * 100).toFixed(1) : 0}%)
                            </p>
                            <p style={{ margin: '8px 0 4px 0', fontWeight: 600 }}>
                              Всего: {data.total}
                            </p>
                            <p style={{ margin: '4px 0', color: data.successRate >= 90 ? '#2e7d32' : data.successRate >= 70 ? '#ed6c02' : '#d32f2f' }}>
                              Успешность: {data.successRate.toFixed(1)}%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="successful"
                    name="Успешные"
                    fill="#2e7d32"
                    stackId="a"
                  />
                  <Bar
                    dataKey="failed"
                    name="Ошибки"
                    fill="#d32f2f"
                    stackId="a"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Статистика
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Самое загруженное время
                    </Typography>
                    <Typography variant="h6">
                      {chartView === 'hourly'
                        ? hourlyData.reduce((max, item) => item.total > max.total ? item : max, hourlyData[0])?.hour || 'N/A'
                        : weeklyData.reduce((max, item) => item.total > max.total ? item : max, weeklyData[0])?.day || 'N/A'
                      }
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Максимум обработок
                    </Typography>
                    <Typography variant="h6">
                      {Math.max(...(chartView === 'hourly' ? hourlyData : weeklyData).map(d => d.total))}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Средняя успешность
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {(
                        (chartView === 'hourly' ? hourlyData : weeklyData)
                          .reduce((sum, item) => sum + item.successRate, 0) /
                        (chartView === 'hourly' ? hourlyData : weeklyData).length
                      ).toFixed(1)}%
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Всего обработок
                    </Typography>
                    <Typography variant="h6">
                      {(chartView === 'hourly' ? hourlyData : weeklyData)
                        .reduce((sum, item) => sum + item.total, 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
